import { Clock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import type { PendingArrival } from "@/features/intake/api/types"
import { formatNumber, kgToMt } from "@/lib/format"

interface IntakeKpiStripProps {
  pending: PendingArrival[]
}

interface MetricProps {
  label: string
  value: React.ReactNode
}

function Metric({ label, value }: MetricProps) {
  return (
    <div className="space-y-1">
      <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wide">
        {label}
      </div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  )
}

export function IntakeKpiStrip({ pending }: IntakeKpiStripProps) {
  const { t } = useTranslation("intake")

  const totalKg = pending.reduce(
    (sum, p) => sum + (p.netKilogramsAttached ?? 0),
    0,
  )
  const totalMt = kgToMt(totalKg)

  const perCooperative = pending.reduce<Record<string, number>>((acc, p) => {
    const name = p.orderId?.groupId?.name ?? null
    if (!name) return acc
    acc[name] = (acc[name] ?? 0) + 1
    return acc
  }, {})
  const cooperativeChips = Object.entries(perCooperative).sort((a, b) =>
    a[0].localeCompare(b[0]),
  )

  return (
    <Card className="px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 flex size-9 flex-shrink-0 items-center justify-center rounded-md">
            <Clock className="size-4" />
          </div>
          <Metric
            label={t("kpis.incoming")}
            value={t("kpis.incomingTrucks", { count: pending.length })}
          />
        </div>

        <Metric
          label={t("kpis.totalExpected")}
          value={`${formatNumber(totalMt, 1)} MT`}
        />

        {cooperativeChips.length > 0 ? (
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {cooperativeChips.map(([name, count]) => (
              <Badge
                key={name}
                variant="outline"
                className="bg-background text-[11px] font-normal"
              >
                {name}
                <span className="text-muted-foreground ml-1.5 tabular-nums">
                  {count}
                </span>
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  )
}
